import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import meApi from 'api/modules/meApi';
import auth from 'api/core/auth';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const { tenant: urlTenant, warehouse: urlWarehouse } = useParams();

  const [user, setUser] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [warehousePermissions, setWarehousePermissions] = useState([]);

  // Ref to track the previous tenant ID for detecting tenant changes
  const prevTenantIdRef = useRef(null);
  const navigate = useNavigate();
  // 초기 로드 - URL 기반 접근 방식으로 변경됨
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        if (auth.isAuthenticated()) {
          const me = await meApi.getMe();
          setUser(me);

          const tenants = me.tenants || [];
          if (!tenants.length) {
            toast.error('No tenant found for this user.');
            auth.clearAuth();
            return;
          }

          // Determine the list of all available warehouses based on user type
          let allWarehouses = [];
          if (me.is_superuser) {
            // Superuser: warehouses are nested within each tenant
            allWarehouses = tenants.flatMap(
              t => t.warehouse_permissions?.map(p => p.warehouse) || [],
            );
          } else {
            // Normal user: warehouses are at the top-level
            allWarehouses =
              me.warehouse_permissions?.map(p => p.warehouse) || [];
          }
          setWarehousePermissions(me.warehouse_permissions || []);

          if (!allWarehouses.length) {
            toast.error('No warehouse found for this user.');
            auth.clearAuth();
            return;
          }

          // Select active tenant and warehouse
          const tenant =
            tenants.find(t => t.domains?.some(d => d.domain === urlTenant)) ||
            tenants[0];
          const warehouse =
            allWarehouses.find(w => w.domain === urlWarehouse) ||
            allWarehouses[0];

          setSelectedTenant(tenant);
          setSelectedWarehouse(warehouse);

          // Navigate away from login page if we are authenticated and have a valid tenant/warehouse
          const currentPath = window.location.pathname;
          if (
            (currentPath === '/login' || currentPath === '/') &&
            tenant &&
            warehouse
          ) {
            const redirectPath = `/${tenant.domains?.[0]?.domain}/${warehouse.domain}`;
            navigate(redirectPath, { replace: true });
          }
        }
      } catch (err) {
        console.error('[UserProvider] init error', err);
        toast.error('Failed to initialize user session. Please login again.');
        auth.clearAuth(); // Clear auth if init fails
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [urlTenant, urlWarehouse, refreshKey, navigate]);

  // For superusers, update warehouse permissions when tenant changes
  useEffect(() => {
    if (user?.is_superuser && selectedTenant) {
      // Find the tenant in the user's tenants array
      const tenant = user.tenants.find(t => t.id === selectedTenant.id);
      if (tenant && tenant.warehouse_permissions) {
        // Update warehouse permissions based on the selected tenant
        setWarehousePermissions(tenant.warehouse_permissions);

        // Check if tenant has changed by comparing with the previous tenant ID
        const tenantChanged = prevTenantIdRef.current !== selectedTenant.id;

        // Only update selectedWarehouse when tenant changes
        if (tenantChanged) {
          const firstWarehouse = tenant.warehouse_permissions[0]?.warehouse;
          if (firstWarehouse) {
            setSelectedWarehouse(firstWarehouse);
          }
        }

        // Update the previous tenant ID ref
        prevTenantIdRef.current = selectedTenant.id;
      }
    }
  }, [user, selectedTenant]);

  const value = useMemo(
    () => ({
      user,
      selectedTenant,
      selectedWarehouse,
      setSelectedTenant,
      setSelectedWarehouse,
      isLoading,
      isSuperuser: !!user?.is_superuser,
      isAdmin: !!user?.is_admin,
      isManager: !!user?.is_manager,
      refresh: () => setRefreshKey(k => k + 1),
      warehouse_permissions: warehousePermissions,
    }),
    [
      user,
      selectedTenant,
      selectedWarehouse,
      isLoading,
      setSelectedTenant,
      setSelectedWarehouse,
      warehousePermissions,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => useContext(UserContext);
